/**
 * Data operations for the chart's editor grid.
 *
 * Kept out of the control component so the array-shuffling is testable by eye
 * and the panel stays about layout. Every function returns new arrays — the
 * attributes are treated as immutable, as the block editor expects.
 */

import { MAX_SERIES } from '../lib/chart-config';

/**
 * Set one cell.
 *
 * @param {Array}  series      Current series.
 * @param {number} seriesIndex Series index.
 * @param {number} cellIndex   Category index.
 * @param {string} raw         Raw input value.
 * @return {Array} New series.
 */
export function setCell( series, seriesIndex, cellIndex, raw ) {
	const value = raw === '' || raw === '-' ? 0 : parseFloat( raw );

	return series.map( ( one, index ) => {
		if ( index !== seriesIndex ) {
			return one;
		}
		const data = [ ...( one.data || [] ) ];
		data[ cellIndex ] = isFinite( value ) ? value : 0;
		return { ...one, data };
	} );
}

/**
 * Update one field on one series.
 *
 * @param {Array}  series      Current series.
 * @param {number} seriesIndex Series index.
 * @param {Object} patch       Fields to merge.
 * @return {Array} New series.
 */
export function patchSeries( series, seriesIndex, patch ) {
	return series.map( ( one, index ) =>
		index === seriesIndex ? { ...one, ...patch } : one
	);
}

/**
 * Add a category, giving every series a zero for it.
 *
 * @param {Array} categories Current categories.
 * @param {Array} series     Current series.
 * @return {{categories: Array, series: Array}} New data.
 */
export function addCategory( categories, series ) {
	return {
		categories: [ ...categories, `Item ${ categories.length + 1 }` ],
		series: series.map( ( one ) => ( {
			...one,
			data: [ ...( one.data || [] ), 0 ],
		} ) ),
	};
}

/**
 * Remove a category and the matching cell from every series.
 *
 * @param {Array}  categories Current categories.
 * @param {Array}  series     Current series.
 * @param {number} index      Category index.
 * @return {{categories: Array, series: Array}} New data.
 */
export function removeCategory( categories, series, index ) {
	return {
		categories: categories.filter( ( _, i ) => i !== index ),
		series: series.map( ( one ) => ( {
			...one,
			data: ( one.data || [] ).filter( ( _, i ) => i !== index ),
		} ) ),
	};
}

/**
 * Add a series.
 *
 * The cap is the palette's slot count, not an arbitrary limit: past eight there
 * is no ninth hue that stays distinguishable under colour-vision deficiency, so
 * the honest answer is to fold the tail into an "Other" row or split the chart.
 *
 * @param {Array} series     Current series.
 * @param {number} cellCount Category count.
 * @return {Array} New series.
 */
export function addSeries( series, cellCount ) {
	if ( series.length >= MAX_SERIES ) {
		return series;
	}

	return [
		...series,
		{
			label: `Series ${ series.length + 1 }`,
			color: '',
			data: new Array( cellCount ).fill( 0 ),
		},
	];
}

/**
 * Remove a series, keeping at least one.
 *
 * @param {Array}  series Current series.
 * @param {number} index  Series index.
 * @return {Array} New series.
 */
export function removeSeries( series, index ) {
	if ( series.length <= 1 ) {
		return series;
	}
	return series.filter( ( _, i ) => i !== index );
}

/**
 * Render the current data as CSV, for the bulk-edit box.
 *
 * @param {Array}  categories   Categories.
 * @param {Array}  series       Series.
 * @param {string} categoryHead Header for the first column.
 * @return {string} CSV.
 */
export function toCsv( categories, series, categoryHead ) {
	const header = [ categoryHead, ...series.map( ( one ) => one.label ) ];
	const rows = categories.map( ( category, index ) => [
		category,
		...series.map( ( one ) => ( one.data || [] )[ index ] ?? 0 ),
	] );

	return [ header, ...rows ].map( ( row ) => row.map( escapeCell ).join( ',' ) ).join( '\n' );
}

/**
 * Quote a cell only when it needs it.
 *
 * @param {*} value Cell value.
 * @return {string} CSV cell.
 */
function escapeCell( value ) {
	const text = String( value ?? '' );
	return /[",\n]/.test( text ) ? `"${ text.replace( /"/g, '""' ) }"` : text;
}

/**
 * Parse the bulk-edit box back into categories and series.
 *
 * First row is the header (its first cell is ignored — it names the category
 * column), first column is the category. Anything unparseable becomes 0 rather
 * than breaking the chart, and the series colours already chosen are kept.
 *
 * @param {string} csv      CSV text.
 * @param {Array}  existing Current series, for colour continuity.
 * @return {{categories: Array, series: Array}|null} Parsed data, or null if empty.
 */
export function fromCsv( csv, existing = [] ) {
	const rows = parseRows( csv );

	if ( rows.length < 2 ) {
		return null;
	}

	const labels = rows[ 0 ].slice( 1, MAX_SERIES + 1 );
	const body = rows.slice( 1 ).filter( ( row ) => row.length > 1 );

	if ( ! labels.length || ! body.length ) {
		return null;
	}

	return {
		categories: body.map( ( row ) => row[ 0 ] ),
		series: labels.map( ( label, column ) => ( {
			label,
			color: existing[ column ]?.color ?? '',
			data: body.map( ( row ) => {
				const value = parseFloat(
					String( row[ column + 1 ] ?? '' ).replace( /[,\s]/g, '' )
				);
				return isFinite( value ) ? value : 0;
			} ),
		} ) ),
	};
}

/**
 * Split CSV text into rows of cells, honouring quoted cells.
 *
 * Small and deliberate rather than a dependency: the input is a textarea a
 * person pasted a spreadsheet into, not a file format that needs a parser.
 *
 * @param {string} csv CSV text.
 * @return {Array} Rows of cells.
 */
function parseRows( csv ) {
	const rows = [];
	let row = [];
	let cell = '';
	let quoted = false;

	const pushCell = () => {
		row.push( cell.trim() );
		cell = '';
	};
	const pushRow = () => {
		pushCell();
		if ( row.some( ( value ) => '' !== value ) ) {
			rows.push( row );
		}
		row = [];
	};

	for ( let i = 0; i < csv.length; i++ ) {
		const char = csv[ i ];

		if ( quoted ) {
			if ( char === '"' ) {
				if ( csv[ i + 1 ] === '"' ) {
					cell += '"';
					i++;
				} else {
					quoted = false;
				}
			} else {
				cell += char;
			}
			continue;
		}

		if ( char === '"' ) {
			quoted = true;
		} else if ( char === ',' || char === '\t' ) {
			pushCell();
		} else if ( char === '\n' ) {
			pushRow();
		} else if ( char !== '\r' ) {
			cell += char;
		}
	}

	pushRow();

	return rows;
}
